<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import {
  listRoleApplications,
  approveRoleApplication,
  rejectRoleApplication,
  type RoleApplicationListItem,
} from '../api';

type StatusTab = 'PENDING' | 'APPROVED' | 'REJECTED';
const tab = ref<StatusTab>('PENDING');
const loading = ref(false);
const list = ref<RoleApplicationListItem[]>([]);
const current = ref<RoleApplicationListItem | null>(null);

async function reload() {
  loading.value = true;
  try {
    list.value = await listRoleApplications(tab.value);
    current.value = null;
  } catch (e) {
    ElMessage.error(normalizeError(e));
  } finally {
    loading.value = false;
  }
}

function openDetail(item: RoleApplicationListItem) {
  current.value = item;
}

async function onApprove() {
  if (!current.value) return;
  const item = current.value;
  try {
    await ElMessageBox.confirm(
      `确认授予该圈友「${item.targetRole}」身份?此后可登录对应后台。`,
      '批准叠加申请',
      { confirmButtonText: '批准', cancelButtonText: '取消', type: 'success' },
    );
  } catch {
    return;
  }
  try {
    await approveRoleApplication(item.id);
    ElMessage.success('已批准,用户已获得目标角色');
    await reload();
  } catch (e) {
    ElMessage.error(normalizeError(e));
  }
}

async function onReject() {
  if (!current.value) return;
  const item = current.value;
  try {
    const { value } = await ElMessageBox.prompt('请填写驳回原因(用户会看到)', '驳回申请', {
      inputType: 'textarea',
      inputPlaceholder: '如:营业执照未上传 / 未标明经营类目 / 与现有角色冲突',
      inputValidator: (v) => (v && v.trim().length >= 2 ? true : '至少 2 字'),
      confirmButtonText: '驳回',
      cancelButtonText: '取消',
    });
    if (!value) return;
    await rejectRoleApplication(item.id, value.trim());
    ElMessage.success('已驳回');
    await reload();
  } catch (e) {
    if ((e as { message?: string })?.message === 'cancel') return;
    ElMessage.error(normalizeError(e));
  }
}

function normalizeError(e: unknown): string {
  const err = e as { message?: string; code?: string };
  return err?.message || err?.code || '未知错误';
}

function formatTime(s: string | null): string {
  if (!s) return '-';
  return new Date(s).toLocaleString('zh-CN');
}

function roleLabel(r: 'MERCHANT' | 'TUTOR'): string {
  return r === 'MERCHANT' ? '商家' : '导师';
}

onMounted(() => {
  reload();
});
</script>

<template>
  <div class="audit-pane">
    <el-tabs v-model="tab" @tab-change="reload" class="tabs">
      <el-tab-pane label="待审核" name="PENDING" />
      <el-tab-pane label="已批准" name="APPROVED" />
      <el-tab-pane label="已驳回" name="REJECTED" />
    </el-tabs>

    <div class="layout">
      <div class="list" v-loading="loading">
        <el-empty v-if="!list.length && !loading" description="暂无申请" />
        <div
          v-for="item of list"
          :key="item.id"
          class="list-item"
          :class="{ active: current?.id === item.id }"
          @click="openDetail(item)"
        >
          <div class="list-row">
            <span class="name">{{ item.user.displayName || '(未设置昵称)' }}</span>
            <el-tag size="small" :type="item.targetRole === 'MERCHANT' ? 'warning' : 'success'">
              申请 {{ roleLabel(item.targetRole) }}
            </el-tag>
          </div>
          <div class="list-sub">
            <span v-if="item.user.schoolName">{{ item.user.schoolName }}</span>
            <span v-if="item.user.enrollYear">· {{ item.user.enrollYear }}届</span>
            <span>· {{ formatTime(item.createdAt) }}</span>
          </div>
          <div v-if="item.status === 'REJECTED' && item.rejectReason" class="list-reject">
            驳回原因:{{ item.rejectReason }}
          </div>
        </div>
      </div>

      <div class="detail">
        <div v-if="!current" class="detail-empty">
          <el-empty description="选一条申请查看详情" />
        </div>
        <template v-else>
          <div class="detail-header">
            <div class="title">
              {{ current.user.displayName || '(未设置昵称)' }}
              <span class="muted">申请</span>
              <el-tag :type="current.targetRole === 'MERCHANT' ? 'warning' : 'success'">
                {{ roleLabel(current.targetRole) }}
              </el-tag>
            </div>
            <div class="subtitle">
              用户 ID <code>{{ current.user.id }}</code> · 账户
              <el-tag size="small">{{ current.user.status }}</el-tag>
            </div>
            <div class="subtitle" v-if="current.user.schoolName">
              {{ current.user.schoolName }} · {{ current.user.enrollYear }}届
            </div>
          </div>

          <div class="section">
            <div class="section-title">申请说明</div>
            <div class="reason-box">{{ current.applyReason }}</div>
          </div>

          <div v-if="current.status === 'PENDING'" class="detail-actions">
            <el-button type="success" size="large" @click="onApprove">批准</el-button>
            <el-button type="danger" size="large" @click="onReject">驳回</el-button>
          </div>
          <div v-else class="detail-handled">
            <span v-if="current.status === 'APPROVED'" class="tag-approved">✓ 已批准</span>
            <span v-else-if="current.status === 'REJECTED'" class="tag-rejected">
              ✕ 已驳回:{{ current.rejectReason }}
            </span>
            <div class="muted" v-if="current.reviewedAt">
              处理时间:{{ formatTime(current.reviewedAt) }}
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.audit-pane { display: flex; flex-direction: column; height: calc(100vh - 80px); }
.tabs { margin-bottom: 12px; }
.layout { display: flex; flex: 1; gap: 16px; overflow: hidden; }
.list { width: 360px; overflow-y: auto; background: #fff; border-radius: 8px; padding: 8px; }
.list-item { padding: 12px; border-radius: 6px; cursor: pointer; margin-bottom: 6px; border: 1px solid transparent; }
.list-item:hover { background: #f5f5f7; }
.list-item.active { background: #e6f4ff; border-color: #409eff; }
.list-row { display: flex; justify-content: space-between; align-items: center; }
.name { font-weight: 600; }
.list-sub { margin-top: 4px; font-size: 12px; color: #86868b; }
.list-reject { margin-top: 6px; font-size: 12px; color: #b42020; }

.detail { flex: 1; overflow-y: auto; background: #fff; border-radius: 8px; padding: 24px; }
.detail-empty { height: 100%; display: flex; align-items: center; justify-content: center; }
.detail-header .title { font-size: 22px; font-weight: 600; display: flex; align-items: center; gap: 10px; }
.detail-header .subtitle { margin-top: 6px; color: #666; font-size: 13px; }
.detail-header .subtitle code { background: #f5f5f7; padding: 2px 6px; border-radius: 4px; font-size: 12px; }

.section { margin-top: 24px; }
.section-title { font-size: 14px; color: #86868b; margin-bottom: 6px; }
.reason-box {
  background: #f5f5f7;
  border-radius: 6px;
  padding: 16px;
  white-space: pre-wrap;
  line-height: 1.6;
  font-size: 14px;
}

.detail-actions { margin-top: 32px; display: flex; gap: 16px; }
.detail-handled { margin-top: 24px; }
.tag-approved { color: #1e7c3e; font-size: 18px; font-weight: 600; }
.tag-rejected { color: #b42020; font-size: 16px; }
.muted { color: #86868b; font-size: 13px; }
</style>
